import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { StardogCloud } from 'src/ui/graph/types';

export const DEMO_CONNECTION_INDEX = 12345;
export const DEMO_KIT_DB_DEFAULT = 'marketplace';

export enum InstanceFlavors {
  MICRO = 'micro',
  // small is deprecated for new endpoints. New endpoints use small-vbx
  SMALL = 'small',
  SMALL_VBX = 'small-vbx',
  MEDIUM = 'medium',
}

export enum StripeBillingType {
  SUBSCRIPTION = 'subscription',
  METERED = 'metered',
}

export enum ConnectionStatus {
  'PENDING',
  'CONNECTED',
  'INVALID',
  'DISCONNECTED',
}

/**
 * Simplified connection object, we really only care about the
 * username and endpoint we don't store tokens or passwords.
 */
export type Connection = {
  id?: string;
  index?: number;
  name: string;
  username: string;
  endpoint: string;
  internalEndpoint?: string;
  useSSO?: boolean;
  password?: string;
  token?: string;
  status: ConnectionStatus;
  isStardogCloud?: boolean;
  isStardogFree?: boolean;
  cloud?: StardogCloud;
  isPortal?: boolean;
  isLaunchpad?: boolean;
  ssoTokenExchangeScope?: string | null;
  usesOrgSso?: boolean | null;
};

export const disconnected: Connection = {
  id: '',
  name: '',
  username: '',
  endpoint: '',
  useSSO: false,
  status: ConnectionStatus.DISCONNECTED,
};

export interface ConnectionState {
  editIndex: number;
  ownerEmail: string | null;
  isBrowserAuthFailure: boolean;
  isDegraded: boolean;
  isUnauthorized: boolean;
  ssoErrorCode: string | null;
}

const initialState = (): ConnectionState => ({
  editIndex: -1,
  ownerEmail: null,
  isBrowserAuthFailure: false,
  isDegraded: false,
  isUnauthorized: false,
  ssoErrorCode: null,
});

export const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    clearConnection: (state) => {
      state.editIndex = -1;
    },
    editConnection: (state, action: PayloadAction<number>) => {
      state.editIndex = action.payload;
    },
    deleteConnection: (state, action: PayloadAction<number>) => {
      state.editIndex = action.payload;
    },
    connectionInvalid: (state, action: PayloadAction<number>) => {
      state.editIndex = action.payload;
    },
    setConnectionBrowserAuthFailed: (state, action: PayloadAction<boolean>) => {
      state.isBrowserAuthFailure = action.payload;
    },
    setConnectionDegraded: (state, action: PayloadAction<boolean>) => {
      state.isDegraded = action.payload;
    },
    setConnectionOwnerEmail: (state, action: PayloadAction<string>) => {
      state.ownerEmail = action.payload;
    },
    setConnectionUnauthorized: (state, action: PayloadAction<boolean>) => {
      state.isUnauthorized = action.payload;
    },
    setConnectionSSOError: (state, action: PayloadAction<string | null>) => {
      state.ssoErrorCode = action.payload;
    },
  },
});

export const {
  clearConnection,
  connectionInvalid,
  deleteConnection,
  editConnection,
  setConnectionBrowserAuthFailed,
  setConnectionDegraded,
  setConnectionOwnerEmail,
  setConnectionSSOError,
  setConnectionUnauthorized,
} = connectionSlice.actions;

export const { getInitialState } = connectionSlice;
